// HLS+AES downloader que delega o I/O binário pro plugin nativo (VxlBatchDownloader).
//
// O JS faz apenas: fetch dos manifestos (texto pequeno), parse, monta o batch,
// aguarda o batchCompleted, reescreve o manifest com paths locais e devolve a URL.
//
// Toda a baixaria pesada (segmentos .ts e chave AES) acontece em background nativo
// (URLSession.background no iOS, ForegroundService no Android), sem passar pelo
// JS bridge — o que evita o crash de memória da versão JS-puro anterior, que
// bufferizava segmentos em base64 no Filesystem.writeFile.

import { Filesystem, Encoding } from '@capacitor/filesystem';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';
import { subscribeBatch } from '@/services/download/vxlBatchEventBus';
import { parseMasterManifest, parseMediaManifest, rewriteManifest } from '@/services/download/hlsManifestParser';
import { STORAGE_DIR, safeLoId, destDirFor, ensureDir, computeOfflineSize } from '@/services/offline/offlinePaths';
import * as telemetry from '@/services/download/downloadTelemetry';

// Concurrency default: 4 → 6 (2026-06-01) → 8 (2026-06-09) → 6 (2026-06-09, revertido).
// Antes o cap era forçado pra 4 em batches cifrados por causa da cifragem
// in-memory iOS (segmento inteiro em RAM × 4 = picos ~60MB). Depois do
// refactor pra streaming cipher iOS (KeyVault.encryptSegment), o pico ficou
// constante ~64KB/thread → cap de 4 deixou de ser necessário.
// 8 foi REVERTIDO: com 8 conexões, o origin (Wowza/Akamai) segurava a 7ª e a 8ª
// — log mostrou seg_6 e seg_7 (= conexões 7 e 8) travados ~12s no fim do batch
// enquanto os outros 251 voavam, deixando a barra "presa" perto de 100%. 6 é o
// ponto onde o origin entrega sem estrangular. Só afeta HLS (N segmentos);
// PDF/áudio são 1 arquivo = 1 conexão.
const DEFAULT_CONCURRENCY = 6;

// Margem aplicada à estimativa de bytes do batch (BANDWIDTH é o pico, não a
// média — o real costuma ficar 5-15% abaixo). Multiplicar por 1.2 cobre essa
// folga e ainda deixa headroom pra: chave AES (~16B), manifest reescrito,
// padding PKCS7 da cifragem (até 16 bytes por segmento), e arredondamento de
// filesystem (alocação em blocos).
const SIZE_ESTIMATE_OVERHEAD = 1.2;

// Algoritmo de cifragem aplicado aos segmentos baixados. Tem que ser AES-128-CBC
// porque o VHS (videojs/http-streaming) só decifra HLS via #EXT-X-KEY:METHOD=AES-128
// que é exatamente isso. CTR/GCM quebrariam o playback.
const ENCRYPTION_ALGORITHM = 'aes-128-cbc';

// Reserva fixa pra não estourar o disco quando ele já está quase cheio — se
// sobrar menos que isso após o download, iOS começa a expulsar caches e
// Android pode rejeitar writes com ENOSPC mesmo com bytes "disponíveis".
const FREE_SPACE_HEADROOM_BYTES = 100 * 1024 * 1024; // 100 MB
// Piso exigido quando não deu pra estimar o tamanho (manifesto sem BANDWIDTH).
// Não garante que a aula cabe — garante que não se começa a baixar com o disco
// no fim, que é o caso em que o batch morre no meio e deixa parcial.
const UNKNOWN_SIZE_MIN_FREE_BYTES = 500 * 1024 * 1024; // 500 MB

// Delay entre cancelar o batch e fazer cleanup/rmdir. Permite que OkHttp
// (Android) e URLSession (iOS) propaguem a IOException da cancel e fechem
// os file descriptors dos `.tmp` em flight ANTES do rmdir tentar varrer o
// diretório. Sem esse gap, rmdir disputa com `out.write(...)` no Android e
// pode deixar arquivos órfãos no disco.
const CANCEL_CLEANUP_DELAY_MS = 300;

// Sinaliza que o broadcast é hospedado em provider externo (YouTube=2, Vimeo=4,
// MP4 progressivo=5). Sem HLS+AES no Wowza, não há manifest que possamos baixar
// e cifrar localmente — a aula só roda online com o player do provider.
// Tipo dedicado pra UI poder mostrar um toast informativo em vez de erro
// genérico de rede (que sugeriria "tente de novo").
export class ExternalProviderError extends Error {
  constructor (provider) {
    super('external-provider');
    this.name = 'ExternalProviderError';
    this.code = 'external-provider';
    this.provider = provider;
  }
}

export class InsufficientDiskSpaceError extends Error {
  constructor (estimatedBytes, availableBytes) {
    super('insufficient-disk-space');
    this.name = 'InsufficientDiskSpaceError';
    this.code = 'insufficient-disk-space';
    this.estimatedBytes = estimatedBytes;
    this.availableBytes = availableBytes;
  }
}

// Sinaliza que o batch foi cancelado (via remove() ou cancel() externo). Não
// é erro real — é ação intencional do user. Telemetria conta como `cancelled`,
// não como `failed`, pra não polluir agregados de taxa de erro.
//
// O plugin nativo emite batchFailed com error='cancelled' nessa situação;
// awaitBatch rebobina pra essa classe.
export class CancelledError extends Error {
  constructor () {
    super('cancelled');
    this.name = 'CancelledError';
    this.code = 'cancelled';
  }
}

// bytes ≈ (BANDWIDTH / 8) * duração_total — fórmula direta a partir dos
// metadados do manifesto. Não exige nenhum HEAD/GET extra. Erro típico
// ±10-15% (BANDWIDTH é o pico declarado; o real flutua pra baixo).
export function estimateDownloadBytes (bandwidth, totalDuration) {
  if (!bandwidth || !totalDuration) return 0;
  return Math.ceil((bandwidth / 8) * totalDuration * SIZE_ESTIMATE_OVERHEAD);
}

async function getFreeDiskBytes () {
  try {
    const { bytes } = await VxlBatchDownloader.getFreeDiskSize();
    return typeof bytes === 'number' ? bytes : 0;
  } catch (e) {
    console.warn('[hlsNativeDownloader] getFreeDiskSize falhou:', e?.message || e);
    // Sem leitura confiável do disco, retornamos 0 → check falha → aborta.
    // Preferir abortar a deixar o download começar e estourar no meio,
    // deixando lixo parcial no sandbox.
    return 0;
  }
}

// Resolve URL do master, baixa master+media manifests, parseia, estima bytes.
// É o trabalho compartilhado entre o preflight (chamado no clique) e o
// download() (chamado na vez da fila). Não toca o disco — só rede + parse.
//
// Lança ExternalProviderError se o broadcast estiver hospedado em YouTube,
// Vimeo ou MP4 progressivo (codProviderExterno != 3). Outros erros (rede,
// 4xx/5xx, manifest mal-formado) propagam normalmente.
async function fetchManifestInfo (broadcastId, token, customerId, options = {}) {
  const preferQuality = options.preferQuality || 'low';
  const masterUrl = await fetchHlsUrl(broadcastId, token, customerId);

  const masterText = await fetchText(masterUrl);
  const variant = parseMasterManifest(masterText, masterUrl, { preferQuality });
  const variantUrl = variant ? variant.url : masterUrl;
  const variantBandwidth = variant ? variant.bandwidth : 0;
  // Altura da resolução escolhida — útil pra telemetria mostrar "1080p" em
  // vez de só bandwidth. Pode ser 0 se o STREAM-INF não trouxe RESOLUTION.
  const variantHeight = variant ? (variant.height || 0) : 0;
  const mediaText = await fetchText(variantUrl);
  const { keyUrl, segments, totalDuration, isMaster, sawExtinf } = parseMediaManifest(mediaText, variantUrl);

  // Falhar aqui é o ponto. Um master que o parseMasterManifest não conseguiu
  // ler cai neste fetch como se fosse media playlist; sem estas guardas as
  // .m3u8 dos variants viravam "segmentos", o batch baixava, o manifest local
  // era escrito e o download terminava reportando SUCESSO sobre conteúdo que
  // não toca. O mesmo vale pra resposta 200 que não é playlist nenhuma —
  // portal cativo devolvendo HTML, por exemplo.
  if (isMaster) {
    throw new Error('O manifesto de mídia é na verdade um master playlist — seleção de variant falhou');
  }
  if (!sawExtinf) {
    throw new Error('Resposta não é um media playlist HLS (nenhum #EXTINF encontrado)');
  }
  if (!segments.length) throw new Error('Nenhum segmento encontrado no media manifest');

  return {
    masterText,
    variantUrl,
    variantHeight,
    mediaText,
    segments,
    keyUrl,
    totalDuration,
    estimatedBytes: estimateDownloadBytes(variantBandwidth, totalDuration),
    // Carimbo pro download() decidir se pode reaproveitar em vez de refetchar
    // (ver MANIFEST_REUSE_TTL_MS). As URLs de segmento são assinadas e expiram.
    fetchedAt: Date.now()
  };
}

// Janela em que o resultado do preflight ainda serve pro download(). Curta de
// propósito: as URLs de segmento são assinadas (Wowza/Akamai) e expiram, e com
// a fila em concurrency=1 uma aula pode esperar minutos atrás de outras. Dentro
// da janela economizamos 3 round-trips encadeados; fora dela, refetch normal.
const MANIFEST_REUSE_TTL_MS = 60 * 1000;

function isManifestFresh (info) {
  return !!(info && Array.isArray(info.segments) && info.segments.length &&
    typeof info.fetchedAt === 'number' && (Date.now() - info.fetchedAt) < MANIFEST_REUSE_TTL_MS);
}

function getCoreMediaBaseUrl () {
  return process.env.VUE_APP_ENV === 'staging'
    ? 'https://vdplayer.st.voxeldigital.com.br/'
    : 'https://vdplayer.voxeldigital.com.br/v2/';
}

// Schedule de backoff entre tentativas (em ms). Tamanho do array = (attempts - 1),
// porque a primeira tentativa não tem delay. Total no pior caso: 500+1000+2000 = 3.5s
// de espera + 3 round-trips. Aceitável pra rede instável (4G de elevador, viagem)
// sem segurar o user por muito tempo.
const NETWORK_RETRY_BACKOFFS_MS = [500, 1000, 2000];

// Códigos HTTP que NÃO devem disparar retry (resposta vai mudar com retentativa?
// Não). 4xx exceto 408 (Request Timeout) e 429 (Too Many Requests) — esses dois
// fazem sentido reterytar.
function shouldRetryHttpStatus (status) {
  if (status < 400) return false; // sucesso, não veio aqui
  if (status === 408 || status === 429) return true;
  if (status >= 500) return true;
  return false; // 401, 403, 404, 4xx genérico = problema definitivo
}

// Wrapper genérico de retry com backoff. Aceita uma função async e retenta até
// `attempts` vezes (default 3) com pausas entre tentativas. Erros não-recuperáveis
// (ExternalProviderError, 4xx do backend) abortam imediatamente sem retry.
async function withRetry (fn, { attempts = 3 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      // Erros de domínio que não vão melhorar com retry — propaga já.
      if (err instanceof ExternalProviderError) throw err;
      // Se for HTTP status com info de status, verifica retryability.
      const status = err && err.httpStatus;
      if (typeof status === 'number' && !shouldRetryHttpStatus(status)) throw err;

      // Última tentativa: propaga.
      if (i === attempts - 1) break;
      // Sleep com backoff antes da próxima tentativa.
      const delay = NETWORK_RETRY_BACKOFFS_MS[i] || 2000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastErr;
}

async function fetchHlsUrl (broadcastId, token, customerId) {
  const baseUrl = getCoreMediaBaseUrl();
  return withRetry(async () => {
    // CapacitorHttp em vez de axios — consistência com fetchText (todo o
    // fluxo HTTP aqui passa pelo bridge nativo do Capacitor). Evita o axios
    // global compartilhado (auth.service.js seta header `authorization` em
    // axios.defaults.headers.common, que vazaria token JWT pra cá).
    const response = await CapacitorHttp.get({
      url: `${baseUrl}broadcasts/${broadcastId}/url`,
      headers: { Authorization: token, CustomerId: customerId }
    });
    if (response.status >= 400) {
      const err = new Error(`HTTP ${response.status} ao resolver URL HLS`);
      err.httpStatus = response.status;
      throw err;
    }
    // Rede aberta com captive portal intercepta e devolve o HTML do portal com
    // status 200 — o CapacitorHttp entrega string (content-type text/html). Sem
    // este guard, a string caía no acesso de shape abaixo e virava um erro sem
    // palavra-chave de rede → classificado como FALHA REAL → cleanup apagava os
    // parciais. Portal é condição de REDE: prefixo "network:" faz o
    // isNetworkError do store pausar (parciais preservados, auto-resume).
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        throw new Error('network: resposta do /url não é JSON — portal cativo na rede?');
      }
    }
    data = data || {};
    // codProviderExterno: 2=YouTube, 3=WCS/HLS, 4=Vimeo, 5=MP4 progressivo.
    // Só 3 (WCS) tem manifest HLS que conseguimos baixar/cifrar. Os demais ficam
    // hospedados em CDN do provider — não conseguimos persistir localmente nem
    // por contrato (DMCA/TOS). Lança ExternalProviderError pra que o caller
    // mostre toast informativo em vez de tratar como erro genérico de rede.
    // Esse erro é detectado pelo withRetry e NÃO retentado.
    const provider = data.provider?.codProviderExterno;
    if (provider !== undefined && provider !== 3) {
      throw new ExternalProviderError(provider);
    }
    const streams = data.listVideoStream;
    if (!streams || !streams.length) {
      // Distinção que decide entre pausar e falhar de vez: se o `provider`
      // veio no corpo, foi a API DO VDPLAYER que respondeu "sem streams" —
      // retentar não muda (falha real). Corpo sem NENHUM campo do contrato é
      // resposta que não veio da nossa API (portal/proxy) — pausa de rede.
      if (provider === undefined) {
        throw new Error('network: resposta sem o shape da API do vdplayer — portal cativo na rede?');
      }
      throw new Error('Nenhum stream HLS disponível para este broadcast');
    }
    return streams[0].stream;
  });
}

async function fetchText (url) {
  return withRetry(async () => {
    const response = await CapacitorHttp.get({ url });
    if (response.status >= 400) {
      // Anexa httpStatus pra withRetry decidir se retenta (5xx/408/429 sim,
      // outros 4xx não).
      const err = new Error(`HTTP ${response.status} ao buscar manifesto`);
      err.httpStatus = response.status;
      throw err;
    }
    // CapacitorHttp pode auto-parsear o corpo conforme o content-type e devolver
    // objeto/array em vez de string. Coage pra string pra o `.split('\n')` do
    // parser de manifesto não lançar TypeError não-tratado: um corpo inesperado
    // vira um erro de domínio normal ("nenhum stream"), tratado pelo fluxo de
    // falha em vez de crash. (Manifesto m3u8 real sempre chega string; isto é
    // defensivo pra servidor mal-configurado.)
    const text = typeof response.data === 'string' ? response.data : String(response.data ?? '');
    // A spec do HLS exige que toda playlist comece com #EXTM3U. Resposta 200
    // sem isso é interceptação (captive portal de rede aberta devolvendo HTML,
    // proxy de hotel) — condição de REDE, não de conteúdo: o "network:" faz o
    // store pausar preservando parciais, em vez da falha real que apagava tudo.
    // trimStart cobre BOM (U+FEFF é whitespace pra JS). As guardas estruturais
    // do fetchManifestInfo (isMaster/sawExtinf) continuam como falha real —
    // depois desta, elas só disparam pra playlist genuína malformada.
    if (!text.trimStart().startsWith('#EXTM3U')) {
      throw new Error('network: resposta não é um manifesto HLS (#EXTM3U ausente) — portal cativo na rede?');
    }
    return text;
  });
}

function batchIdFor (loId) {
  return `lo_${safeLoId(loId)}`;
}

// Subscreve eventos do batch (progress/completed/failed) e devolve uma promise
// que resolve no completed e rejeita no failed. Antes retornava só a promise —
// se o caller falhasse ENTRE `subscribeBatch` e o evento de finalização (ex:
// enqueue lançando), o listener ficava pendurado pra sempre.
//
// Agora devolve { promise, dispose } pra que o caller possa garantir
// unsubscribe em todos os paths via `finally { dispose(); }`. `dispose` é
// idempotente — pode ser chamado várias vezes sem efeito colateral.
//
// Erros do plugin com `error === 'cancelled'` viram `CancelledError` (não erro
// real — ver classe acima). Outros erros viram Error genérico com a mensagem
// do plugin + lista de itens falhados.
function awaitBatch (batchId, total, onProgress) {
  let resolveFn, rejectFn;
  const promise = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });

  // Guarda razões dos itemFailed. O batchFailed agregado só traz a lista
  // de ids ("Items failed: ..."), perdendo o motivo — sem isso, uma pausa
  // wifi-only com poucos segmentos restantes (< 3) não casaria nas heurísticas
  // do isNetworkError e o download seria apagado em vez de pausado.
  //
  // Mantém o PRIMEIRO erro pausável (rede/wifi-only) visto + o último erro
  // qualquer. O batch propaga preferindo o pausável: sem isso, uma falha fatal
  // posterior (ex: "disk full") mascarava uma pausa de rede anterior e o
  // store classificava o batch inteiro como falha real, apagando parciais
  // válidos que deveriam ser preservados pra retry.
  let lastItemError = null;
  let pausableError = null;
  // Espelha a regex do `isNetworkError` em downloadHelpers.js. Duplicada aqui
  // pra não criar dependência circular (downloadHelpers não pode importar
  // deste módulo, mas este módulo importar de lá criaria acoplamento maior).
  const PAUSABLE_RE = /network|timeout|unreachable|connect|IOException|NSURLError|ECONN|offline|wifi-only|HTTP 5\d\d/i;

  let disposed = false;
  const unsubscribe = subscribeBatch(batchId, {
    progress: ({ done, total: t, percent }) => {
      const pct = percent != null ? Math.round(percent * 100) : Math.round((done / (t || total)) * 100);
      if (typeof onProgress === 'function') onProgress(pct);
    },
    itemFailed: ({ error }) => {
      if (!error) return;
      lastItemError = error;
      if (!pausableError && PAUSABLE_RE.test(error)) {
        pausableError = error;
      }
    },
    completed: () => {
      dispose();
      resolveFn({ ok: true });
    },
    failed: ({ error, failedItems }) => {
      dispose();
      if (error === 'cancelled') {
        rejectFn(new CancelledError());
      } else {
        // Prefere o erro pausável (preserva parciais via cleanup gentil) sobre
        // o último erro (que pode ser uma falha fatal posterior).
        const reason = pausableError || lastItemError;
        const reasonStr = reason ? ` (${reason})` : '';
        rejectFn(new Error(`Batch ${batchId} falhou: ${error || 'desconhecido'}${reasonStr}${failedItems ? ' [' + failedItems.join(',') + ']' : ''}`));
      }
    }
  });

  function dispose () {
    if (disposed) return;
    disposed = true;
    try { unsubscribe(); } catch {}
  }

  return { promise, dispose };
}

// Verifica se o batch (1 ou N aulas) cabe no disco. Lança
// InsufficientDiskSpaceError com os totais já somados se não couber.
//
// Estimativa ausente (0) NÃO desliga o check. Ela some sempre que o manifesto
// não traz BANDWIDTH — master com uma única rendition, #EXT-X-STREAM-INF sem o
// atributo — e nesse caso o `return null` de antes deixava passar QUALQUER
// download com QUALQUER espaço livre, justamente o cenário em que a estimativa
// falha. Sem número pra comparar ainda dá pra exigir uma folga mínima, que é o
// que separa "não sei o tamanho" de "não tenho onde escrever".
async function assertFitsDisk (totalEstimated) {
  const availableBytes = await getFreeDiskBytes();

  if (totalEstimated <= 0) {
    if (availableBytes < UNKNOWN_SIZE_MIN_FREE_BYTES) {
      throw new InsufficientDiskSpaceError(UNKNOWN_SIZE_MIN_FREE_BYTES, availableBytes);
    }
    return { estimatedBytes: 0, availableBytes };
  }

  if (totalEstimated + FREE_SPACE_HEADROOM_BYTES > availableBytes) {
    throw new InsufficientDiskSpaceError(totalEstimated, availableBytes);
  }
  return { estimatedBytes: totalEstimated, availableBytes };
}

export const hlsNativeDownloader = {
  // Faz fetch + parse + soma de bytes esperados de N aulas em paralelo, depois
  // 1 leitura de disco e 1 comparação. Ideal pro botão "Baixar turma" — o user
  // descobre antes de qualquer item ser enfileirado se a turma inteira cabe.
  //
  // lessons: [{ broadcastId, token, customerId }]
  // Aulas sem broadcast (PDF/quiz) ou com provider != HLS são silenciosamente
  // ignoradas — não consomem disco. Erros de rede em um item são
  // contabilizados como 0 (não vamos abortar a turma toda por um manifesto
  // que falhou; o download real vai re-tentar e o catch lida).
  // `options.preferQuality` PRECISA ser a mesma qualidade que o download vai
  // pedir: sem isso a estimativa saía no variant 'low' (default) enquanto o
  // download baixava o escolhido pelo user. Numa turma em 'alta' isso subestima
  // o disco em ~10x — o preflight diz "cabe", e o disco estoura lá pela 6ª aula,
  // deixando a turma pela metade depois de o user ter esperado o download todo.
  async preflightBatch (lessons, options = {}) {
    if (!Capacitor.isNativePlatform()) return null;
    if (!lessons || !lessons.length) return { estimatedBytes: 0, availableBytes: 0 };

    const infos = await Promise.all(
      lessons.map(l =>
        fetchManifestInfo(l.broadcastId, l.token, l.customerId, options)
          .catch(e => {
            console.warn('[hlsNativeDownloader] preflight item falhou (segue):', e?.message || e);
            return null;
          })
      )
    );
    const totalEstimated = infos
      .filter(i => i && i.estimatedBytes > 0)
      .reduce((sum, i) => sum + i.estimatedBytes, 0);

    return (await assertFitsDisk(totalEstimated)) || { estimatedBytes: totalEstimated, availableBytes: 0 };
  },

  // Versão pra 1 LO (botão "Baixar aula"). Equivalente a preflightBatch com 1
  // item, mas explícita pra leitura.
  //
  // Retorna { externalProvider: true } se o broadcast é YouTube/Vimeo/MP4 —
  // a action no store usa essa flag pra abortar cedo com toast informativo,
  // antes de marcar status downloading e enfileirar. Outros erros (rede,
  // 401, manifest inválido) propagam pro caller tratar como erro real.
  // Devolve também o `info` do manifesto pro caller repassar ao download() —
  // sem isso o download() refazia os MESMOS 3 round-trips encadeados
  // (getUrl → master.m3u8 → variant.m3u8) segundos depois, dobrando a espera
  // até o primeiro byte. Ver MANIFEST_REUSE_TTL_MS.
  async preflight (broadcastId, token, customerId, options = {}) {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const info = await fetchManifestInfo(broadcastId, token, customerId, options);
      const disk = (await assertFitsDisk(info.estimatedBytes)) || { estimatedBytes: info.estimatedBytes, availableBytes: 0 };
      return { ...disk, info };
    } catch (err) {
      if (err instanceof ExternalProviderError) {
        return { externalProvider: true, provider: err.provider, estimatedBytes: 0, availableBytes: 0 };
      }
      throw err;
    }
  },

  // Baixa o HLS+AES de uma aula e devolve URL local pro player.
  //
  // Params (todos opcionais exceto loId/broadcastId/token/customerId):
  //   - loId, broadcastId, token, customerId — credenciais Wowza/CoreMedia
  //   - onProgress(percent) — callback de 0-100; chamado várias vezes durante
  //     o download (debounce do plugin ~500ms)
  //   - enrollId — pra associar loId↔enrollId no índice nativo (lease gate)
  //   - quality — 'low' | 'medium' | 'high' (default 'low' por retrocompat)
  //   - connectionType — só pra telemetria ('wifi'|'cellular'|'unknown')
  //   - network — 'wifi-only' bloqueia o download nativo em cellular (pausa se
  //     a rede virar móvel mid-batch). undefined/null = sem restrição.
  //   - concurrency — override da concorrência de segmentos do batch (prioridade
  //     de reprodução usa 1). undefined = DEFAULT_CONCURRENCY. Clampado a
  //     [1, DEFAULT_CONCURRENCY]: acima de 6 o origin estrangula (ver histórico
  //     no topo). Snapshotado no batch nativo — não muda em voo. HOJE SÓ O
  //     ANDROID HONRA: o plugin iOS aceita e ignora o valor (o limite real lá é
  //     o httpMaximumConnectionsPerHost da sessão; gate por batch é fase 2).
  async download ({
    loId,
    broadcastId,
    token,
    customerId,
    onProgress,
    enrollId,
    quality,
    connectionType,
    network,
    concurrency,
    // Resultado do fetchManifestInfo já feito pelo preflight. Reaproveitado se
    // ainda estiver dentro da janela (MANIFEST_REUSE_TTL_MS); senão refetch.
    manifestInfo
  } = {}) {
    if (!Capacitor.isNativePlatform()) return null;

    // Sanitiza loId uma vez e reutiliza pra batchId, destDir, keyAlias e URI
    // do scheme. Garantir consistência aqui evita que o KeyVault armazene a
    // chave com nome "12345" enquanto o manifest aponta pra "../foo".
    const safeId = safeLoId(loId);
    if (!safeId) {
      throw new Error('loId inválido pra download offline');
    }
    const lessonDir = destDirFor(safeId);
    const batchId = batchIdFor(safeId);

    // Clamp defensivo: o plugin Android não tem teto próprio (só max(1,...)),
    // e acima de DEFAULT_CONCURRENCY o origin estrangula conexões extras.
    const effectiveConcurrency = Number.isFinite(concurrency)
      ? Math.max(1, Math.min(Math.floor(concurrency), DEFAULT_CONCURRENCY))
      : DEFAULT_CONCURRENCY;

    // Sessão de telemetria — registra timestamps em pontos-chave e persiste
    // no fim (complete, failed ou cancelled). Não bloqueia download em caso
    // de erro (todos os marks são try/catch silenciosos internamente).
    const tele = telemetry.start({
      loId: safeId,
      enrollId: enrollId != null ? String(enrollId) : null,
      format: 'hls',
      quality: quality || null,
      connectionType: connectionType || null,
      concurrency: effectiveConcurrency
    });

    // Wrap o onProgress do caller pra marcar firstByte na 1ª vez que o
    // progresso > 0 chega. Mantém o callback original funcionando.
    const wrappedOnProgress = (p) => {
      if (typeof p === 'number' && p > 0) tele.markFirstByte();
      if (typeof onProgress === 'function') onProgress(p);
    };

    // Handle do batch; obrigatório `dispose()` em qualquer path pra evitar
    // listener leak (antes acumulavam +3 listeners a cada retry quando
    // enqueue lançava antes de o batch chegar a completed/failed).
    let batchHandle = null;

    try {
      // 1. Resolver manifesto + estimar tamanho (1 request HLS URL + 2 fetches de manifest).
      //    Cada call a download() refetcha o manifest do zero — significa que URLs
      //    assinadas (Wowza/Akamai) que expiraram entre pause e resume são auto-
      //    renovadas aqui, antes do enqueue. Plugin nativo recebe URLs frescas e
      //    o reconcile pega os segmentos já no disco por filename (não por URL),
      //    então parciais cifrados com URLs antigas continuam válidos.
      // fetchManifestInfo lança ExternalProviderError pra providers não-HLS;
      // o caller (action no store) cata e mostra toast informativo.
      // Reaproveita o manifesto do preflight quando ainda fresco: eram 3
      // round-trips ENCADEADOS refeitos segundos após o preflight ter feito os
      // mesmos, o que dobrava o tempo até o primeiro byte aparecer. Fora da
      // janela (fila cheia, resume horas depois) refetcha — que é o motivo de o
      // refetch existir: renovar URL assinada expirada.
      const reusable = isManifestFresh(manifestInfo);
      const info = reusable
        ? manifestInfo
        : await fetchManifestInfo(broadcastId, token, customerId, {
            preferQuality: quality || 'low'
          });
      const { mediaText, segments, estimatedBytes, variantHeight } = info;
      tele.markManifestReady({ segmentCount: segments.length, variantHeight });

      // 2. Re-checar disco aqui (defesa em profundidade): preflight já rodou no
      //    clique, mas o disco pode ter encolhido enquanto a fila processava
      //    (outras aulas baixadas em paralelo, app de fora consumindo espaço).
      //    Se metadados não bastam pra estimar, pulamos — preferimos tentar a
      //    abortar por incerteza.
      await assertFitsDisk(estimatedBytes);

      // 3. Garantir o diretório
      await ensureDir(lessonDir);

      // 4. Montar items do batch — somente segmentos. A chave AES de cifragem
      //    LOCAL é gerada/persistida pelo KeyVault nativo (hardware-backed), não
      //    baixada de URL. `keyUrl` do Wowza atual é zero (sem cupertinoEncryption);
      //    se algum dia voltar, o manifest local sobrescreve a URI pro nosso scheme
      //    e o `enc.key` do Wowza deixa de ser usado.
      const items = segments.map((seg, idx) => ({
        id: `seg_${idx}`,
        url: seg.url,
        filename: seg.filename,
        // Sequence number do segmento — o plugin nativo usa pra derivar o IV da
        // cifragem AES-128-CBC (16 bytes, big-endian). Tem que bater com o IV que
        // o VHS reconstrói no playback, derivado do EXT-X-MEDIA-SEQUENCE start (0)
        // + posição do segmento.
        segmentIndex: idx
      }));

      // 5. Atacha listeners (síncrono — não awaita por causa do bug iOS) e enfileira.
      //    Pequena janela: se o batch terminasse antes do native registrar listeners,
      //    perderíamos eventos. Na prática essa janela é microssegundos no native,
      //    e o batch HLS tem 50+ segmentos pra processar, então sem race real.
      batchHandle = awaitBatch(batchId, items.length, wrappedOnProgress);
      const enqueueResult = await VxlBatchDownloader.enqueue({
        batchId,
        destDir: lessonDir,
        concurrency: effectiveConcurrency,
        items,
        encryption: {
          algorithm: ENCRYPTION_ALGORITHM,
          keyAlias: safeId
        },
        // Associa loId (safeId) ao enrollId no índice nativo (LoEnrollIndex). O
        // scheme handler vxlkey:// usa isso pra carregar o lease correto antes
        // de devolver a chave AES. Sem enrollId, o playback fica bloqueado
        // (defesa em profundidade contra downloads sem associação).
        ...(enrollId != null ? { enrollId: String(enrollId) } : {}),
        // Restrição de rede aplicada nativamente (iOS allowsCellularAccess /
        // Android ConnectivityManager). Omitido quando sem restrição.
        ...(network ? { network } : {})
      });
      // enqueue = onde o NATIVO faz reconcileExistingFiles (stat de cada
      // segmento já em disco) antes de começar a baixar. Suspeito nº1 se este
      // marco for grande.

      // Se o plugin diz que já tem batch rodando com esse ID (user clicou
      // baixar 2× rápido, ou re-clique após app voltar do background), o NOVO
      // listener pode ter perdido o evento `batchCompleted` (já disparado).
      // Resultado antes deste fix: awaitBatch ficava pendurado eternamente.
      //
      // Conserto: verifica o estado real do batch via getStatus. Se já terminou
      // (todos os items done), pula o await e segue pro writeFile. Senão,
      // confia que o evento ainda virá (batch in-flight).
      if (enqueueResult && enqueueResult.alreadyRunning) {
        try {
          const status = await VxlBatchDownloader.getStatus({ batchId });
          const allDone = status && status.total > 0 && status.done >= status.total;
          if (status && status.state === 'completed') {
            // Já completou — não há evento futuro pra aguardar.
            batchHandle.dispose();
          } else if (status && status.state === 'failed') {
            // Já falhou — sem retry automático; propaga erro.
            batchHandle.dispose();
            const failedList = Array.isArray(status.failedItems) ? status.failedItems : [];
            throw new Error(`Batch ${batchId} já estava failed (alreadyRunning): [${failedList.join(',')}]`);
          } else if (allDone) {
            // State não veio explicit 'completed' mas done==total — race entre
            // checkBatchCompletion e nosso getStatus; trata como completed.
            batchHandle.dispose();
          } else {
            // Ainda em progresso — listener vai receber o batchCompleted normal.
            await batchHandle.promise;
          }
        } catch (e) {
          // Se getStatus falhar (rede ou bridge), fallback pro await tradicional.
          // Pior caso: hang volta. Mas só nesse subcase (getStatus erro), que é
          // raríssimo. Logamos pra diagnóstico.
          console.warn('[hlsNativeDownloader] getStatus após alreadyRunning falhou; aguardando evento:', e?.message || e);
          await batchHandle.promise;
        }
      } else {
        await batchHandle.promise;
      }

      // 6. Reescrever manifest com paths locais + injetar #EXT-X-KEY apontando pro
      //    scheme local. VHS vai fetchar vxlkey://<safeId> ao tocar e o nativo
      //    responde com a File Key plaintext do KeyVault.
      // `vxlkey://<safeId>` é o scheme custom servido pelo nativo
      // (VxlKeySchemeHandler iOS / VxlKeyWebViewClient Android). Quando o VHS
      // pede a chave nesse URI, o handler descifra a File Key do KeyVault
      // hardware-backed e responde com 16 bytes plaintext — sem tocar o disco.
      const localManifest = rewriteManifest(mediaText, segments, {
        localKeyUri: `vxlkey://${safeId}`
      });
      await Filesystem.writeFile({
        path: `${lessonDir}/manifest.m3u8`,
        data: localManifest,
        directory: STORAGE_DIR,
        encoding: Encoding.UTF8
      });

      // 7. Limpar estado persistido no nativo (arquivos permanecem)
      try {
        await VxlBatchDownloader.cleanupBatch({ batchId });
      } catch {
        // best-effort
      }

      // 8. Devolver URL que o CoreMedia/VHS consegue tocar
      const { uri } = await Filesystem.getUri({
        path: `${lessonDir}/manifest.m3u8`,
        directory: STORAGE_DIR
      });

      // Mede tamanho real do diretório pra throughput na telemetria, mas SEM
      // bloquear a resolução do download — antes esse await travava a
      // finalização (o usuário via a barra "presa" perto de 100% por segundos).
      // markComplete só persiste local (idempotente), então rodar depois do
      // return é inofensivo. computeOfflineSize agora é nativo (1 número), mas
      // mantemos fire-and-forget como defesa.
      computeOfflineSize(safeId)
        .then(actualBytes => tele.markComplete({ actualBytes }))
        .catch(() => tele.markComplete({ actualBytes: null }));

      return Capacitor.convertFileSrc(uri);
    } catch (e) {
      // Cancelamento (user clicou remover durante download) NÃO é falha —
      // separa pra que dashboards de erro não fiquem polluídos. Telemetria
      // marca como cancelled + complete sem actualBytes (download não terminou,
      // mas a "sessão" terminou normalmente do ponto de vista do user).
      if (e instanceof CancelledError) {
        tele.markCancelled();
        tele.markComplete({ actualBytes: null });
      } else {
        tele.markFailed(e);
      }
      throw e;
    } finally {
      // Garante unsubscribe em TODOS os paths: sucesso, falha de enqueue
      // antes do completed/failed, cancel, etc. Idempotente — chamar depois
      // do dispose interno do completed/failed é no-op.
      if (batchHandle) batchHandle.dispose();
    }
  },

  async remove (loId) {
    // cancela download em andamento (idempotente) e remove arquivos.
    // Os catches loggam ao invés de silenciar — se rmdir falhar, usuário pensa
    // que liberou espaço mas os arquivos continuam ocupando GB no disco.
    const safeId = safeLoId(loId);
    if (!safeId) return;

    try {
      await VxlBatchDownloader.cancel({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[hlsNativeDownloader] cancel batch ${safeId} falhou:`, e?.message || e);
    }
    // Pequena janela pra OkHttp/URLSession propagar IOException de cancel
    // antes do rmdir — ver comentário em CANCEL_CLEANUP_DELAY_MS no topo.
    await new Promise(resolve => setTimeout(resolve, CANCEL_CLEANUP_DELAY_MS));
    try {
      await VxlBatchDownloader.cleanupBatch({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[hlsNativeDownloader] cleanupBatch ${safeId} falhou:`, e?.message || e);
    }
    // Apaga a File Key cifrada do KeyVault. Sem isso, _batchdl_state/keys/<safeId>.bin
    // ficaria órfão ocupando 44 bytes pra sempre (insignificante, mas vira
    // ruído se acumular após muitas remoções).
    try {
      await VxlBatchDownloader.removeKey({ keyAlias: safeId });
    } catch (e) {
      console.warn(`[hlsNativeDownloader] removeKey ${safeId} falhou:`, e?.message || e);
    }
    try {
      await Filesystem.rmdir({
        path: destDirFor(safeId),
        directory: STORAGE_DIR,
        recursive: true
      });
    } catch (e) {
      console.warn(`[hlsNativeDownloader] rmdir ${safeId} falhou (arquivos podem persistir no disco):`, e?.message || e);
    }
  }
};
